import {useEffect, useState} from 'react';
import {useController, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {
  FormInput,
  PasswordInput,
  getPasswordValidation,
} from '@treatwell/ui/foundations';
import type {RulesAPIResponse} from '@treatwell/ui/foundations';
import {fetchPasswordRules} from '@treatwell/ui';
import {fetchChannel} from '../../../../../api/rainbow';
import type {FormInputs} from '../Form';
import styles from './UserDetails.module.css';

export const UserDetails = () => {
  const {t} = useTranslation();
  const [passwordRules, setPasswordRules] = useState<
    RulesAPIResponse | undefined
  >(undefined);
  const {control} = useFormContext<FormInputs>();
  const {
    field: firstName,
    fieldState: {error: firstNameError},
  } = useController({
    control,
    name: 'firstName',
    rules: {required: t('register.userDetails.firstName.required')},
  });
  const {
    field: lastName,
    fieldState: {error: lastNameError},
  } = useController({
    control,
    name: 'lastName',
    rules: {required: t('register.userDetails.lastName.required')},
  });
  const {
    field: email,
    fieldState: {error: emailError},
  } = useController({
    control,
    name: 'email',
    rules: {
      required: t('register.userDetails.email.required'),
      pattern: {
        value: /\S+@\S+\.\S+/,
        message: t('register.errors.emailInvalid'),
      },
    },
  });
  const {
    field: password,
    fieldState: {error: passwordError},
  } = useController({
    control,
    name: 'password',
    rules: {
      required: t('register.userDetails.password.required'),
      validate: (value) => {
        if (!passwordRules) {
          return true;
        }
        return getPasswordValidation(passwordRules, value).errors.length === 0;
      },
    },
  });

  useEffect(() => {
    fetchChannel()
      .then((channel) => channel.locale.split('_')[0])
      .catch(() => 'en')
      .then((language) => fetchPasswordRules({language, context: 'SIGNUP'}))
      .then((response) => setPasswordRules(response.passwordRules))
      .catch(() => {});
  }, []);

  return (
    <>
      <FormInput
        label={t('register.userDetails.firstName.label')}
        autoComplete="given-name"
        isInvalid={Boolean(firstNameError)}
        errorMessage={firstNameError?.message}
        className={styles.firstName}
        {...firstName}
      />
      <FormInput
        label={t('register.userDetails.lastName.label')}
        autoComplete="family-name"
        isInvalid={Boolean(lastNameError)}
        errorMessage={lastNameError?.message}
        className={styles.lastName}
        {...lastName}
      />
      <FormInput
        label={t('register.userDetails.email.label')}
        type="email"
        autoComplete="email"
        isInvalid={Boolean(emailError)}
        errorMessage={emailError?.message}
        className={styles.email}
        {...email}
      />
      <div className={styles.password}>
        <PasswordInput
          label={t('register.userDetails.password.label')}
          autoComplete="new-password"
          showPasswordLabel={t('register.userDetails.password.showPassword')}
          hidePasswordLabel={t('register.userDetails.password.hidePassword')}
          rules={passwordRules}
          isInvalid={Boolean(passwordError)}
          errorMessage={
            passwordError?.type === 'required'
              ? passwordError.message
              : undefined
          }
          {...password}
        />
      </div>
    </>
  );
};
